/**
 * Zustand store for game state management
 */

import { create } from 'zustand';
import { loadSession, saveSession, clearSession } from '../lib/session';
import type { GameState, ServerEvent, FinalScore } from '../types/generated';

export interface ChatMessage {
  id: string;
  playerId: string;
  playerName: string;
  colorIndex?: number;  // Player's color index for Jackbox-style colors
  text: string;
  timestamp: number;
  isSystem?: boolean;
  isLookup?: boolean;
  isValid?: boolean;
}

export interface Toast {
  id: string;
  type: 'error' | 'success' | 'info';
  message: string;
  duration?: number;
}

// Track toast timeouts for cleanup
const toastTimeouts = new Map<string, number>();

// Track recently claimed word timeouts for cleanup
const recentlyClaimedTimeouts = new Map<string, number>();

interface GameStore {
  // Connection state
  connected: boolean;
  playerId: string | null;
  roomCode: string | null;
  rejoinToken: string | null;
  latency: number;
  reconnecting: boolean;

  // Game state (synced from server)
  gameState: GameState | null;

  // Chat state
  chatMessages: ChatMessage[];
  endGameTimer: number | null; // Unix timestamp when game ends
  resumeCountdownEndsAt: number | null; // Timestamp when resume countdown ends

  // UI state
  lastEvent: ServerEvent | null;
  claimStatus: 'idle' | 'claiming' | 'success' | 'error';
  claimError: string | null;

  // Toast notifications
  toasts: Toast[];

  // Loading states
  isCreatingRoom: boolean;
  isJoiningRoom: boolean;
  isStartingGame: boolean;
  isFlippingTile: boolean;

  // Steal selection state (UX Advisor: critical missing feature)
  selectedWordsToSteal: Set<string>;

  // Typing highlight - which middle tile indices are being used by current input
  typingHighlightIndices: Set<number>;

  // Animation state - track recently claimed words for animations
  recentlyClaimedWordIds: Set<string>;

  // Word validation state (real-time dictionary check)
  wordValidation: {
    word: string;
    isValid: boolean | null; // null = checking
  } | null;

  // Most recent tile flip - used to mark only the freshly flipped tile as "new"
  // middleLength is the length of the middle right after the flip
  lastFlip: { letter: string; middleLength: number } | null;

  // Final results captured when GAME_ENDED arrives (immune to later events)
  finalResults: { finalScores: FinalScore[]; winner: FinalScore | null } | null;

  // Actions
  setConnected: (connected: boolean) => void;
  setPlayerId: (id: string | null) => void;
  setRoomCode: (code: string | null) => void;
  setRejoinToken: (token: string | null) => void;
  setLatency: (latency: number) => void;
  setReconnecting: (reconnecting: boolean) => void;
  setGameState: (state: GameState | null) => void;
  setLastEvent: (event: ServerEvent) => void;
  setClaimStatus: (status: 'idle' | 'claiming' | 'success' | 'error', error?: string) => void;
  addChatMessage: (message: ChatMessage) => void;
  setEndGameTimer: (endsAt: number | null) => void;
  setResumeCountdownEndsAt: (endsAt: number | null) => void;
  toggleWordToSteal: (wordId: string) => void;
  clearWordsToSteal: () => void;
  setTypingHighlightIndices: (indices: Set<number>) => void;
  addRecentlyClaimedWord: (wordId: string) => void;
  setWordValidation: (validation: { word: string; isValid: boolean | null } | null) => void;
  setFinalResults: (results: { finalScores: FinalScore[]; winner: FinalScore | null } | null) => void;

  // Toast actions
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;

  // Loading state actions
  setCreatingRoom: (loading: boolean) => void;
  setJoiningRoom: (loading: boolean) => void;
  setStartingGame: (loading: boolean) => void;
  setFlippingTile: (loading: boolean) => void;

  // Reset
  reset: () => void;
}

const initialSession = loadSession();

export const useGameStore = create<GameStore>((set, get) => ({
  // Initial state (a saved session lets a refreshed tab rejoin its game)
  connected: false,
  playerId: initialSession?.playerId ?? null,
  roomCode: initialSession?.roomCode ?? null,
  rejoinToken: initialSession?.rejoinToken ?? null,
  latency: 0,
  reconnecting: false,
  gameState: null,
  chatMessages: [],
  endGameTimer: null,
  resumeCountdownEndsAt: null,
  lastEvent: null,
  claimStatus: 'idle',
  claimError: null,
  toasts: [],
  isCreatingRoom: false,
  isJoiningRoom: false,
  isStartingGame: false,
  isFlippingTile: false,
  selectedWordsToSteal: new Set<string>(),
  typingHighlightIndices: new Set<number>(),
  recentlyClaimedWordIds: new Set<string>(),
  wordValidation: null,
  lastFlip: null,
  finalResults: null,

  // Actions
  setConnected: (connected) => set({ connected }),
  setPlayerId: (playerId) => set({ playerId }),
  setRoomCode: (roomCode) => set({ roomCode }),
  setRejoinToken: (rejoinToken) => set({ rejoinToken }),
  setLatency: (latency) => set({ latency }),
  setReconnecting: (reconnecting) => set({ reconnecting }),
  // Replace server-synced state. Local-only UI state (playerId, chat, toasts,
  // claim status, etc.) is preserved; selections referencing words that no
  // longer exist are pruned and derived timers are recomputed.
  setGameState: (gameState) =>
    set((state) => {
      if (!gameState) {
        return { gameState: null, endGameTimer: null, finalResults: null };
      }
      const existingWordIds = new Set<string>();
      for (const words of Object.values(gameState.playerWords)) {
        for (const w of words) existingWordIds.add(w.id);
      }
      const prunedSelection = [...state.selectedWordsToSteal].filter((id) => existingWordIds.has(id));
      const endGameTimer =
        gameState.status === 'playing' && gameState.endTimerStart != null
          ? gameState.endTimerStart + gameState.endTimerMs
          : null;
      return {
        gameState,
        endGameTimer,
        selectedWordsToSteal:
          prunedSelection.length === state.selectedWordsToSteal.size
            ? state.selectedWordsToSteal
            : new Set(prunedSelection),
        finalResults: gameState.status === 'ended' ? state.finalResults : null,
      };
    }),
  setLastEvent: (lastEvent) => set({ lastEvent }),
  setClaimStatus: (claimStatus, error) =>
    set({ claimStatus, claimError: error || null }),
  addChatMessage: (message) =>
    set((state) => ({
      chatMessages: [...state.chatMessages.slice(-99), message], // Keep last 100 messages
    })),
  setEndGameTimer: (endGameTimer) => set({ endGameTimer }),
  setResumeCountdownEndsAt: (resumeCountdownEndsAt) => set({ resumeCountdownEndsAt }),
  toggleWordToSteal: (wordId) =>
    set((state) => {
      const newSet = new Set(state.selectedWordsToSteal);
      if (newSet.has(wordId)) {
        newSet.delete(wordId);
      } else if (newSet.size < 2) {
        // Max 2 words can be stolen at once
        newSet.add(wordId);
      }
      return { selectedWordsToSteal: newSet };
    }),
  clearWordsToSteal: () => set({ selectedWordsToSteal: new Set<string>() }),
  setTypingHighlightIndices: (typingHighlightIndices) =>
    set((state) => {
      // Skip the update (and re-renders) when the set contents are unchanged
      const current = state.typingHighlightIndices;
      if (current.size === typingHighlightIndices.size) {
        let same = true;
        for (const i of typingHighlightIndices) {
          if (!current.has(i)) {
            same = false;
            break;
          }
        }
        if (same) return state;
      }
      return { typingHighlightIndices };
    }),
  addRecentlyClaimedWord: (wordId: string) =>
    set((state) => {
      const newSet = new Set(state.recentlyClaimedWordIds);
      newSet.add(wordId);

      // Clear any existing timeout for this word (prevents memory leak)
      const existingTimeout = recentlyClaimedTimeouts.get(wordId);
      if (existingTimeout) {
        window.clearTimeout(existingTimeout);
      }

      // Auto-remove after animation completes (1s)
      const timeoutId = window.setTimeout(() => {
        recentlyClaimedTimeouts.delete(wordId);
        useGameStore.setState((s) => {
          const updatedSet = new Set(s.recentlyClaimedWordIds);
          updatedSet.delete(wordId);
          return { recentlyClaimedWordIds: updatedSet };
        });
      }, 1000);
      recentlyClaimedTimeouts.set(wordId, timeoutId);

      return { recentlyClaimedWordIds: newSet };
    }),
  setWordValidation: (wordValidation) => set({ wordValidation }),
  setFinalResults: (finalResults) => set({ finalResults }),

  // Toast actions
  addToast: (toast) =>
    set((state) => {
      // De-duplicate: don't add if same message already exists
      const isDuplicate = state.toasts.some(
        (t) => t.message === toast.message && t.type === toast.type
      );
      if (isDuplicate) {
        return state; // No change
      }

      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newToast = { ...toast, id };

      // Auto-remove after duration (default 5s for errors, 3s for others)
      const duration = toast.duration ?? (toast.type === 'error' ? 5000 : 3000);
      const timeoutId = window.setTimeout(() => {
        toastTimeouts.delete(id);
        get().removeToast(id);
      }, duration);
      toastTimeouts.set(id, timeoutId);

      return { toasts: [...state.toasts, newToast] };
    }),
  removeToast: (id) => {
    // Clear timeout if toast is manually removed before it expires
    const timeoutId = toastTimeouts.get(id);
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      toastTimeouts.delete(id);
    }
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  // Loading state actions
  setCreatingRoom: (isCreatingRoom) => set({ isCreatingRoom }),
  setJoiningRoom: (isJoiningRoom) => set({ isJoiningRoom }),
  setStartingGame: (isStartingGame) => set({ isStartingGame }),
  setFlippingTile: (isFlippingTile) => set({ isFlippingTile }),

  reset: () => {
    // Clean up all pending timeouts to prevent memory leaks
    toastTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    toastTimeouts.clear();
    recentlyClaimedTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    recentlyClaimedTimeouts.clear();

    // NOTE: connection state (connected/latency/reconnecting) is intentionally
    // left untouched - the socket stays up after leaving a room.
    clearSession();
    set({
      playerId: null,
      roomCode: null,
      rejoinToken: null,
      gameState: null,
      chatMessages: [],
      endGameTimer: null,
      resumeCountdownEndsAt: null,
      lastEvent: null,
      claimStatus: 'idle',
      claimError: null,
      toasts: [],
      isCreatingRoom: false,
      isJoiningRoom: false,
      isStartingGame: false,
      isFlippingTile: false,
      selectedWordsToSteal: new Set<string>(),
      typingHighlightIndices: new Set<number>(),
      recentlyClaimedWordIds: new Set<string>(),
      wordValidation: null,
      lastFlip: null,
      finalResults: null,
    });
  },
}));

// ============================================================================
// SELECTORS - pure functions for use with useGameStore(selector)
// ============================================================================

/** Player ID whose turn it currently is (null if not playing / invalid index) */
export function selectCurrentTurnPlayerId(s: GameStore): string | null {
  const gs = s.gameState;
  if (!gs || gs.status !== 'playing') return null;
  const index = gs.currentTurnIndex;
  if (index < 0 || index >= gs.turnOrder.length) return null;
  return gs.turnOrder[index] ?? null;
}

/** True when it is the local player's turn */
export function selectIsMyTurn(s: GameStore): boolean {
  return s.playerId !== null && selectCurrentTurnPlayerId(s) === s.playerId;
}

// Persist the session whenever the player's identity in a room changes
useGameStore.subscribe((state, prev) => {
  if (
    state.playerId === prev.playerId &&
    state.roomCode === prev.roomCode &&
    state.rejoinToken === prev.rejoinToken
  ) {
    return;
  }
  if (state.playerId && state.roomCode && state.rejoinToken) {
    saveSession({ playerId: state.playerId, roomCode: state.roomCode, rejoinToken: state.rejoinToken });
  }
});
